import { HttpStatusCode } from 'axios';
import { useEffect, useState } from 'react';
import { ClipLoader } from 'react-spinners';
import {
  ReferralMetrics,
  ReferralPayoutObject,
  Entity,
} from '../../../../../types/apiTypes';
import useApi from '../../../../../utils/AxiosWrapper';
import { useScreenView } from '../../../../../utils/ScreenViewContext';
import { useUser } from '../../../../../utils/UserContext';
import InviteReferralModal from '../../../vendors/tabs/referrals/widgets/InviteReferralModal';
import ReferralMetricsWidget from '../../../vendors/tabs/referrals/widgets/ReferralMetricsWidget';
import ReferralPayoutSummary from '../../../vendors/tabs/referrals/widgets/ReferralPayoutSummary';
import ReferralTopBar from '../../../vendors/tabs/referrals/widgets/ReferralTopBar';
import ReferralInvitesList from './widgets/ReferralInvitesList';

interface PartnerReferralsOverviewProps {
  userType: string;
  currentPage: string;
  currentTab: string;
  setCurrentTab: (tab: string) => void;
  subTab: string;
  setSubTab: (tab: string) => void;
}

function PartnerReferralsOverview({
  userType,
  currentPage,
  currentTab,
  setCurrentTab,
  subTab,
  setSubTab,
}: PartnerReferralsOverviewProps) {
  const { screenView } = useScreenView();
  const { callProtectedApi } = useApi();
  const { userId, userRecord } = useUser();
  const [referralPopupVisible, setReferralPopupVisible] = useState(false);
  const [loadingMetrics, setLoadingMetrics] = useState(false);
  const [loadingPayoutSummary, setLoadingPayoutSummary] = useState(false);
  const [metrics, setMetrics] = useState<ReferralMetrics | null>(null);
  const [payoutSummary, setPayoutSummary] = useState<
    ReferralPayoutObject[] | null
  >(null);
  useEffect(() => {
    const fetchReferralMetrics = async (partner_id: string) => {
      setLoadingMetrics(true);
      try {
        const response = await callProtectedApi(
          `/referrals/metrics/${userRecord?.userType}/${partner_id}`,
          'get',
          {}
        );
        const data = response?.data?.data?.metrics;
        if (
          data &&
          Object.entries(data)?.length > 0 &&
          response?.status === HttpStatusCode.Ok
        ) {
          setMetrics(data);
        }
      } catch (e: any) {
        console.error('Error occurred while fetching referral metrics: ', e);
      } finally {
        setLoadingMetrics(false);
      }
    };

    const fetchReferralPayoutSummary = async (partner_id: string) => {
      setLoadingPayoutSummary(true);
      try {
        const response = await callProtectedApi(
          `/referrals/payout-summary/${userRecord?.userType}/${partner_id}`,
          'get',
          {}
        );
        const data = response?.data?.data?.summary;
        if (
          data &&
          data?.length > 0 &&
          response?.status === HttpStatusCode.Ok
        ) {
          setPayoutSummary(data);
        }
      } catch (e: any) {
        console.error('Error occurred while fetching referral metrics: ', e);
      } finally {
        setLoadingPayoutSummary(false);
      }
    };

    // Only fetch metrics and payout summary for Partner accounts, not PartnerAgent
    if (userType === Entity.Partner) {
      fetchReferralMetrics(userId ?? '');
      fetchReferralPayoutSummary(userId ?? '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userType]);

  const updateReferralPopupVisible = (visible: boolean) => {
    setReferralPopupVisible(visible);
  };

  const renderScreenView = (screenView: string) => {
    switch (screenView) {
      case 'desktop':
      case 'tablet':
      case 'mobile':
        return (
          <div className="overflow-y-auto overflow-x-hidden sm:w-[95%] md:w-full md:px-4 lg:w-[1148px] lg:max-w-[1148px]">
            {referralPopupVisible && (
              <InviteReferralModal
                onClose={() => setReferralPopupVisible(false)}
                isOpen={referralPopupVisible}
              />
            )}
            {/* <OnboardingModal /> */}
            <div className="flex h-full w-full flex-col space-y-[16px] py-[16px] pb-12">
              <ReferralTopBar
                setReferralPopupVisible={updateReferralPopupVisible}
              />
              {/* Show metrics and payout summary only for Partner accounts */}
              {userType === Entity.Partner ? (
                <>
                  <div className="flex w-full flex-row justify-center">
                    {!loadingMetrics ? (
                      <ReferralMetricsWidget referralMetrics={metrics} />
                    ) : (
                      <div className="flex h-[95px] w-full items-center justify-center rounded-[4px] border-[1px] border-bordergray bg-white">
                        <ClipLoader
                          color="#1D213B"
                          loading={loadingMetrics}
                          size={40}
                          aria-label="Loading Spinner"
                          data-testid="loader"
                          className="flex h-full w-full justify-center"
                        />
                      </div>
                    )}
                  </div>
                  <div className="flex w-full flex-row space-x-4">
                    <ReferralPayoutSummary
                      payoutObjects={payoutSummary ?? []}
                      loadingPayoutSummary={loadingPayoutSummary}
                      addClass="w-[50%]"
                    />{' '}
                  </div>
                </>
              ) : (
                /* For PartnerAgent accounts, show the invite tracking list */
                <ReferralInvitesList userType={userType} />
              )}
            </div>
          </div>
        );
    }
  };
  return <>{renderScreenView(screenView)}</>;
}

export default PartnerReferralsOverview;
